import { AccountInfo } from '@azure/msal-browser';
import { Client, GraphError } from "@microsoft/microsoft-graph-client";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { StorageProviderKey } from '../../models/storageProviderKeys';
import { Keys, messages } from '../../settings';
import { shouldUseRedirect } from '../../utilities/platform';
import IStorageProvider from '../storage/istorageProvider';
import { Context } from '../_globalContext/context';
import { OneDriveInteractionRequiredError } from './MSALAuthenticationProvider';
import * as OneDriveApi from './oneDriveApi';
import * as oneDriveAuth from './oneDriveAuth';

export interface IOneDriveContext {
  isAuthenticated: boolean;
  graphClient: Client;

  /**
   * Signing in. On iOS this navigates the whole page away and the returned
   * promise never settles - see oneDriveAuth. Only callers passing
   * `allowRedirect` can take that path, because being thrown out of the app is
   * a reasonable thing to happen when you press "OneDrive Authorize" and not a
   * reasonable thing to happen when you press play.
   */
  authorize(options?: {
    allowRedirect?: boolean;
    intent?: oneDriveAuth.OneDriveIntent;
  }): Promise<void>;
  /** Whether authorize() can complete here, or only by navigating away. */
  canAuthorizeInPlace: boolean;
  /** A sign-in is in flight. Buttons show it and refuse a second press. */
  isAuthorizing: boolean;
  /**
   * Assumes you have previously completed auth flow.
   * Immediately sets OneDrive state as authenticated, skips api call and auth prompt.
   */
  setIsAuthenticated(): void;
  logout(): void;
  getAccount(): AccountInfo | null;
  handleGraphErrors(err: GraphError): void;

  configuredApi: ReturnType<typeof OneDriveApi.configureOneDriveApi>;
}
export const OneDriveContext = React.createContext({} as IOneDriveContext);

interface Props {
  children: React.ReactNode;
}

export default function OneDriveService({
  children,
}: Props) {

  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const [isAuthenticated, setAuthenticated] = useState(false);
  const [graphClient] = useState(OneDriveApi.getClient);
  const [account, setAccount] = useState<AccountInfo | null>(null);

  const sp = useRef<IStorageProvider>();

  /**
   * Read synchronously by components, so it reads a snapshot rather than MSAL.
   *
   * v1's getAccount() could be called the moment you had the instance. v3
   * requires initialize() to have resolved first, which makes the real answer
   * asynchronous - and providerList wants it during a render. So the account
   * is kept in state, and this hands back whatever we last learned.
   */
  const accountRef = useRef<AccountInfo | null>(null);
  accountRef.current = account;

  const getAccount = useCallback(function () {
    return accountRef.current;
  }, []);

  const adoptSignedInAccount = useCallback(function (signedIn: AccountInfo | null) {
    setAccount(signedIn);
    setAuthenticated(!!signedIn);

    return signedIn;
  }, []);

  useEffect(function autologin() {
    let cancelled = false;

    oneDriveAuth.getMsalApp().then(
      function adoptCachedAccount(app) {
        if (cancelled) {
          return;
        }

        const existing = oneDriveAuth.adoptAccount(app, oneDriveAuth.currentAccount(app));

        if (!existing) {
          return;
        }

        adoptSignedInAccount(existing);

        // A redirect sign-in lands back here, not in the component that
        // started it - /onedrive-redirect renders outside the service tree, so
        // by the time this service exists again the caller that asked for the
        // sign-in is long gone. The intent it left behind is how "set OneDrive
        // as the storage provider" survives that.
        if (oneDriveAuth.takeState().intent.configureProvider) {
          ctxRef.current.configureStorageProvider(StorageProviderKey.OneDrive);
        }
      },
      function ignoreStartupFailure() {
        // Not being signed in to OneDrive is an ordinary state, and MSAL
        // failing to initialise must not stall startup for a Drive or Dropbox
        // user - Google's scripts already make startup fragile enough.
      },
    );

    return function () {
      cancelled = true;
    };
  }, [adoptSignedInAccount]);

  const logout = useCallback(function () {

    function completeLogout() {
      // Nothing useful can be done with a failure here - the point of pressing
      // Logout is to stop being signed in, so say so immediately and let the
      // cache catch up.
      oneDriveAuth.clearLocalSession()
        .catch(function ignoreClearFailure() { });

      setAccount(null);
      setAuthenticated(false);
    }

    if (ctxRef.current.storageProvider.current === sp.current) {
      ctxRef.current.configureStorageProvider();
      ctxRef.current.spLogoutHandling().then(completeLogout);
    }
    else {
      completeLogout();
    }
  }, []);

  const errorHandler = useCallback(async function (err: GraphError) {
    // The auth provider no longer opens a window when it cannot get a token
    // silently - it throws this instead, from wherever the Graph call was
    // made. It reaches here as an ordinary failure, so name it before the
    // GraphError checks get hold of it and report a network problem.
    if (err instanceof OneDriveInteractionRequiredError) {
      ctxRef.current.callSnackbar('Not currently logged in, please login.');
      logout();
    }
    else if (err instanceof GraphError) {
      if (err.statusCode === 401) {
        ctxRef.current.callSnackbar('Not currently logged in, please login.');
        logout();
      } else if (err.statusCode === 404) {
        ctxRef.current.callSnackbar('Unable to find requested resource!');
      } else if (err.statusCode === 413) {
        ctxRef.current.callSnackbar('The request size exceeds the maximum limit.');
      } else if (err.statusCode === 429) {
        ctxRef.current.callSnackbar('Too many OneDrive requests, please try again later.');
      } else if (err.statusCode === 507) {
        ctxRef.current.callSnackbar('The maximum storage quota has been reached.');
      } else if (err.statusCode === 509) {
        ctxRef.current.callSnackbar('App has been throttled for exceeding the maximum bandwidth cap. Please contact fadila@starcraft.is');
      } else if (err.statusCode < 500) {
        ctxRef.current.callSnackbar('There was a problem with your request, please try again later.')
      } else if (err.statusCode >= 500) {
        ctxRef.current.callSnackbar('OneDrive server error! Please try again later.');
      } else {
        ctxRef.current.callSnackbar(err.message);
      }
    }
    else {
      ctxRef.current.callSnackbar((err as any).message || err);
    }
  }, [logout]);

  const configuredApi = useMemo(
    () => OneDriveApi.configureOneDriveApi(graphClient, errorHandler, ctx.flate),
    [graphClient, errorHandler, ctx.flate]
  );
  const configuredApiRef = useRef(configuredApi);
  configuredApiRef.current = configuredApi;

  useEffect(function debug() {
    (global as any)['od'] = graphClient;
  }, [graphClient]);
  useEffect(function registerAsStorageProvider() {
    ctxRef.current.storageProviderMap[StorageProviderKey.OneDrive] = sp;
  }, []);
  useEffect(function updateStorageProvider() {
    sp.current = {
      name: StorageProviderKey.OneDrive,

      // decurry
      load: function load<T>(fileName: string, defaultData: T) {
        if (isAuthenticated) {
          return configuredApiRef.current.getOrCreateDataFile(fileName)(defaultData);
        }
        else {
          ctxRef.current.configureStorageProvider();
          return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
        }
      },
      saveAppData: (fileName: string, fileData: any) => {
        if (isAuthenticated) {
          return configuredApiRef.current.putFile(fileName)(fileData);
        }
        else {
          ctxRef.current.configureStorageProvider();
          return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
        }
      },
    };
  }, [isAuthenticated]);

  const doAuthorize = useCallback(async function (options?: {
    allowRedirect?: boolean;
    intent?: oneDriveAuth.OneDriveIntent;
  }): Promise<void> {

    if (oneDriveAuth.willRedirect(options && options.allowRedirect)) {
      await oneDriveAuth.beginRedirect(options && options.intent);

      // The page is on its way to Microsoft. Hand back a promise that never
      // settles so no caller runs a .then() against a document being torn
      // down - same contract gapiService.authorize has on this path.
      return new Promise<void>(function neverSettles() { });
    }

    adoptSignedInAccount(await oneDriveAuth.beginPopup());

    // Note this now *rejects* when sign-in fails. It used to report the error
    // itself and resolve anyway, which meant providerList's "set as storage
    // provider" ran on the back of a sign-in that had not happened.
  }, [adoptSignedInAccount]);

  const [isAuthorizing, setAuthorizing] = useState(false);
  const authorizeInFlight = useRef<Promise<void>>();

  /**
   * One sign-in at a time.
   *
   * MSAL opens a blank popup and then spends a couple of seconds on authority
   * metadata before anything shows in it; meanwhile the button looked exactly
   * as it did before the press. A second press opened a second popup, which
   * MSAL refuses as interaction_in_progress - so the second caller is handed
   * the first promise instead, and isAuthorizing lets the button say so.
   *
   * On the redirect path the promise never settles and the flag never clears,
   * deliberately: the page is going away, and "Signing in…" is the truth
   * until it does.
   */
  const authorize = useCallback(function (options?: {
    allowRedirect?: boolean;
    intent?: oneDriveAuth.OneDriveIntent;
  }): Promise<void> {

    if (authorizeInFlight.current) {
      return authorizeInFlight.current;
    }

    setAuthorizing(true);

    authorizeInFlight.current = doAuthorize(options).finally(function settled() {
      authorizeInFlight.current = undefined;
      setAuthorizing(false);
    });

    return authorizeInFlight.current;
  }, [doAuthorize]);

  const setIsAuthenticated = useCallback(() => setAuthenticated(true), []);
  const ctxValue = useMemo(() => ({

    isAuthenticated,
    graphClient,

    authorize,
    canAuthorizeInPlace: !shouldUseRedirect(),
    isAuthorizing,
    setIsAuthenticated,
    logout,
    getAccount,
    handleGraphErrors: errorHandler,

    configuredApi,
  }), [
    isAuthenticated,
    graphClient,
    authorize,
    isAuthorizing,
    setIsAuthenticated,
    logout,
    getAccount,
    errorHandler,
    configuredApi,
  ]);

  return (
    <OneDriveContext.Provider value={ctxValue}>
      {children}
    </OneDriveContext.Provider >
  );
}
